import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import FlightWorld, { Chamber } from './FlightWorld';
import ResonanceFinale from './ResonanceFinale';
import { Reveal, useReducedMotion } from '../../animations';
import { recordEvent } from '../../telemetry';
import { useSmoothScroll } from '../../lib/useSmoothScroll';
import { BannerContent } from '../Data/banner';
import { allArticles } from '../Data/articles';
import { LAB_CATALOG } from '../Labs/labCatalog';
import ForgeModel from './ForgeModel';
import ContentPaths from './ContentPaths';
import HeroStreamingShowcase from './HeroStreamingShowcase';
import MatchCutCareerRelay from './MatchCutCareerRelay';
import AgentSystemFilm from '../Media/AgentSystemFilm';
import { ArrowRight } from '../../design/icons';
import { gsap } from '../../animations/gsapClient';
import '../../design/cinematic.css';
import './home.css';

// The corridor is a film, but the page is chrome — both follow the theme.
const INK = 'var(--film-ink)';
const BG = 'var(--film-ground)';

/**
 * The corridor. Each chamber is one stage of the pipeline, inhabited by the
 * agent that owns it. The camera flies through all of them without a cut.
 */
/* Copper, bronze and sage connect the pipeline to the site palette.
   FlightWorld adjusts these hues against the theme's neutral. */
const CHAMBERS: Chamber[] = [
    {
        id: 'ingest', variant: 'cloud', color: '#a8541b', state: 'curious',
        title: 'It all starts as mess',
        body: 'Docs, tickets, transcripts, schemas. Before a model is useful on your data, something has to decide what a unit of meaning even is. That decision is most of the job, and it never makes it onto the slide.',
        tags: ['Chunking', 'Parsing', 'Pipelines'],
        action: { label: 'Try The Gate lab', to: '/labs/the-gate' },
    },
    {
        id: 'embed', variant: 'squircle', color: '#91aa9c', state: 'thinking',
        title: 'Meaning becomes geometry',
        body: 'Every chunk becomes a vector. Similar ideas land near each other, and "find me something like this" collapses into a distance calculation. Either profound or deeply reductive, depending on the day.',
        tags: ['Vector DBs', 'Embeddings', 'ANN'],
        action: { label: 'Run Semantic Search', to: '/labs/semantic-search' },
    },
    {
        id: 'retrieve', variant: 'hexagon', color: '#e9956f', state: 'working',
        title: 'The right context, ranked',
        body: 'Nearest-neighbour is the first draft, not the answer. Reranking and caching decide what reaches the context window, and that decides whether the output is worth reading. Most demos stop just before this part.',
        tags: ['RAG', 'Reranking', 'KV cache'],
        action: { label: 'Inspect RAG retrieval', to: '/article/how-rag-retrieval-actually-works' },
    },
    {
        id: 'attend', variant: 'teardrop', color: '#8a5a34', state: 'thinking',
        title: 'Where the thinking happens',
        body: 'Attention weights every token against every other, layer after layer, and gradients flow back through all of it. Most explanations wave at this part. I work it with real numbers — which is how I found out my own derivation was wrong the first time.',
        tags: ['Transformers', 'Backprop', 'LoRA'],
        action: { label: 'Train in Backprop Forge', to: '/labs/backprop-forge' },
    },
    {
        id: 'ship', variant: 'pill', color: '#b0b5af', state: 'done',
        title: 'Agents that finish the job',
        body: 'The unglamorous half: guardrails, human-in-the-loop, observability. Running on real enterprise data without anyone holding its hand. Nobody has ever demoed observability.',
        tags: ['LangGraph', 'Google ADK', 'MCP'],
        action: { label: 'Read Agentic AI in production', to: '/article/agentic-ai-salesforce' },
    },
    {
        id: 'silicon', variant: 'triangle', color: '#e8946b', state: 'working',
        title: 'And then down to the silicon',
        body: 'Abstractions leak, so I go under them. Evenings are an ESP32-S3 on a breadboard, tiny-gpu RTL in simulation, and an open FPGA toolchain I lose most arguments with. Learn by building, all the way down.',
        tags: ['ESP32-S3', 'iCE40 FPGA', 'RTL', 'yosys'],
        action: { label: 'Explore ML formats & runtimes', to: '/labs/format-tree' },
    },

];

const Home: React.FC = () => {
    const pipelineRef = useRef<HTMLDivElement>(null);
    const finaleRef = useRef<HTMLElement>(null);
    const heroRef = useRef<HTMLElement>(null);
    const reduced = useReducedMotion();
    const latest = allArticles[0];
    useSmoothScroll({ lerp: 0.085 });

    useEffect(() => { recordEvent('page.view', { 'page.name': 'home' }); }, []);

    useEffect(() => {
        if (reduced || !heroRef.current) return;
        const context = gsap.context(() => {
            gsap.fromTo('.home-hero__line', { y: 28, opacity: .4 },
                { y: 0, opacity: 1, duration: .9, stagger: .12, ease: 'expo.out' });
        }, heroRef);
        return () => context.revert();
    }, [reduced]);

    const followPipeline = () => {
        pipelineRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        pipelineRef.current?.focus({ preventScroll: true });
    };

    // The corridor is a long scroll-driven sequence. Not everyone wants to fly
    // it, so the way past it is offered up front rather than after the fact.
    const skipPipeline = () => {
        finaleRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        finaleRef.current?.focus({ preventScroll: true });
        recordEvent('home.pipeline_skip', {});
    };

    return (
        <main id="main" tabIndex={-1} className="bs-page home-flight cinematic-surface" style={{ background: BG, color: INK }}>
            <div className="home-intro">
                <section className="home-hero" ref={heroRef} aria-labelledby="home-heading">
                    <div className="home-hero__copy">
                        <p className="home-hero__signature">{BannerContent.name}<span>Berlin, Germany</span></p>
                        <h1 id="home-heading"><span className="home-hero__line">AI systems,</span><em className="home-hero__line">from the<br />inside out.</em></h1>
                        <p className="home-hero__identity">
                            {BannerContent.role}
                        </p>
                        <p className="home-hero__lede">
                            I connect enterprise platforms with production agents, then work below the abstractions.
                            Retrieval, transformer training, and hardware experiments. Built, inspected, shared.
                        </p>
                        <div className="home-hero__actions">
                            <Link className="bs-btn cinematic-primary" to="/labs" onClick={() => recordEvent('home.cta', { 'cta.to': '/labs' })}>Explore the labs<ArrowRight size={18} /></Link>
                            <Link className="bs-btn" to="/about" onClick={() => recordEvent('home.cta', { 'cta.to': '/about' })}>Meet the builder</Link>
                        </div>
                        <div className="home-hero__metrics" aria-label="Portfolio index">
                            <Link to="/articles" className="home-hero__metric">
                                <strong>{allArticles.length}</strong>
                                <span>Working notes</span>
                            </Link>
                            <Link to="/labs" className="home-hero__metric">
                                <strong>{LAB_CATALOG.length}</strong>
                                <span>Browser labs</span>
                            </Link>
                            <Link to="/cinema" className="home-hero__metric">
                                <strong>11 yrs</strong>
                                <span>3D career flight</span>
                            </Link>
                            <Link to="/fit" className="home-hero__metric">
                                <strong>Fit Check</strong>
                                <span>Match a role</span>
                            </Link>
                        </div>
                        <button className="home-pipeline-link" onClick={followPipeline}>Follow the pipeline<ArrowRight size={16} /></button>
                    </div>
                    <div className="home-hero__art"><ForgeModel /></div>
                </section>

                <HeroStreamingShowcase />

                <ContentPaths />

                <div className="home-experience" aria-label="Professional experience">
                    <div className="home-experience__head">
                        <p>Experience across</p>
                        <Link to="/cinema" className="home-experience__flight tap-hit">Fly 11 years in 3D <ArrowRight size={14} /></Link>
                    </div>
                    <div className="home-experience__stops">
                        <div className="home-experience__stop"><span>Infosys</span><small>Systems Engineer · India</small></div>
                        <div className="home-experience__stop"><span>adidas</span><small>Senior Platform Eng · Germany</small></div>
                        <div className="home-experience__stop"><span>LemonOne</span><small>Lead Engineer · Berlin</small></div>
                        <div className="home-experience__stop home-experience__stop--current"><span>Zalando</span><small>Senior Systems Architect · Berlin</small></div>
                    </div>
                </div>

                <MatchCutCareerRelay />

                <AgentSystemFilm />

                <section className="home-work" aria-label="Explore the work">
                    {latest && (
                        <Link className="home-work__writing" to={`/article/${latest.slug}`} onClick={() => recordEvent('article.open', { 'article.slug': latest.slug, 'article.category': latest.category })}>
                            <div className="home-work__badge">Latest working note · {latest.category}</div>
                            <h2>{latest.title}</h2>
                            <p>{latest.summary}</p>
                            <span>{latest.date} · Read the latest writing<ArrowRight size={16} /></span>
                        </Link>
                    )}
                    <div className="home-work__links">
                        <Link to="/labs/backprop-forge"><span className="home-work__idx" aria-hidden="true">01</span><span><strong>Train a transformer</strong><small>A hand-derived backward pass, running in your browser.</small></span><span aria-hidden="true">↗</span></Link>
                        <Link to="/labs/openui-streaming"><span className="home-work__idx" aria-hidden="true">02</span><span><strong>Stream Generative UI</strong><small>OpenUI Lang 1.0: top-down statement hoisting and live patching in 56% fewer tokens.</small></span><span aria-hidden="true">↗</span></Link>
                        <Link to="/cinema"><span className="home-work__idx" aria-hidden="true">03</span><span><strong>Fly through the career</strong><small>Eleven years, one continuous journey.</small></span><span aria-hidden="true">↗</span></Link>
                        <Link to="/articles"><span className="home-work__idx" aria-hidden="true">04</span><span><strong>Read the working notes</strong><small>Inference, training, and the systems around them.</small></span><span aria-hidden="true">↗</span></Link>
                        <Link to="/fit"><span className="home-work__idx" aria-hidden="true">05</span><span><strong>Run a Fit Check</strong><small>Paste a job description and verify every requirement against published proof.</small></span><span aria-hidden="true">↗</span></Link>
                    </div>
                </section>
            </div>

            <div className="home-pipeline-head" ref={pipelineRef} tabIndex={-1} role="region" aria-label="Inside the pipeline">
                <h2>Inside the pipeline</h2>
                <p>From messy documents to production agents — then down to the silicon. Scroll through the stages that make the system work.</p>
                <button type="button" className="home-pipeline-skip" onClick={skipPipeline}>
                    Skip the flight<ArrowRight size={16} />
                </button>
            </div>
            <FlightWorld chambers={CHAMBERS} scrollPerChamber={0.65} />

            <section className="home-finale" ref={finaleRef} tabIndex={-1} aria-label="Closing">
                <Reveal y={18}>
                    <ResonanceFinale height={500} cycleSeconds={6}>
                        <h2>Curiosity,<br />all the way down.</h2>
                        <p className="home-finale__text">
                            From production agents to transformer gradients—and the rhythms hidden in Euclid&rsquo;s algorithm.
                            I build to understand, then share the working parts.
                        </p>
                    </ResonanceFinale>
                </Reveal>
                <div className="home-invitation">
                    <h2>Have a hard<br /><em>systems problem?</em></h2>
                    <a className="bs-btn cinematic-primary" href={`mailto:${BannerContent.email}`}>Let&rsquo;s talk<ArrowRight size={18} /></a>
                    <p>{BannerContent.email}</p>
                </div>
                <footer className="home-footer">
                    <a className="tap-hit" href={`mailto:${BannerContent.email}`} onClick={() => recordEvent('home.contact', { 'contact.via': 'email' })}>Let&rsquo;s build something ↗</a>
                    <nav aria-label="Connect">
                        <a className="tap-hit" href={BannerContent.linkedin} target="_blank" rel="noopener noreferrer" onClick={() => recordEvent('home.contact', { 'contact.via': 'linkedin' })}>LinkedIn ↗</a>
                        <a className="tap-hit" href={BannerContent.github} target="_blank" rel="noopener noreferrer" onClick={() => recordEvent('home.contact', { 'contact.via': 'github' })}>GitHub ↗</a>
                        <Link className="tap-hit" to="/about">About me ↗</Link>
                    </nav>
                </footer>
            </section>
        </main>
    );
};

export default Home;
